/*
 * moonling.js — Moonling-Begleiter (Sprite-Atlas-Laufzeit).
 *
 * Aufbau:
 *   · Platzierung   findet einen freien Punkt; wenn keiner frei ist, den am
 *                   wenigsten verdeckten — die Figur verschwindet nie einfach.
 *   · Regie         wählt kontextabhängig Ort und Szene: Zeigerposition,
 *                   Tastaturfokus, sichtbarer Abschnitt, Wartezeit.
 *   · Verfolgung    verankerte Szenen kleben beim Scrollen an ihrem Element.
 *   · Wiedergabe    Abbruch nur bei echter Layoutänderung, nicht bei jedem
 *                   Scroll oder DOM-Mutation.
 * Reduced-Motion, Hintergrund-Tabs, Overlays und Druck sind berücksichtigt.
 */
(function () {
  'use strict';
  const clamp=(n,a,b)=>Math.max(a,Math.min(b,n));
  const intersects=(a,b,gap=6)=>a.left<b.right+gap&&a.right>b.left-gap&&a.top<b.bottom+gap&&a.bottom>b.top-gap;
  const area=(a,b,grow=0)=>Math.max(0,Math.min(a.right,b.right+grow)-Math.max(a.left,b.left-grow))*Math.max(0,Math.min(a.bottom,b.bottom+grow)-Math.max(a.top,b.top-grow));
  const permitted=s=>s.enabled&&!s.hidden&&!s.paused&&!s.reduced&&!s.background&&!s.blocked&&s.mobileAllowed;
  const frameAt=(elapsed,clip)=>clamp(Math.floor(elapsed*clip.fps/1000),0,clip.frames-1);
  const validImage=(image,clip)=>image.naturalWidth%clip.cols===0&&image.naturalHeight%clip.rows===0&&image.naturalWidth/clip.cols===image.naturalHeight/clip.rows&&image.naturalWidth/clip.cols>=128;
  const aliases={'cart-add':'treasure',success:'happy',celebrate:'happy',boop:'surprised',curious:'inspect',sleepy:'sleep',walk:'wander','modal-peek':'peek','map-peek':'peek','logo-sit':'sit','cta-hover':'hover','cta-click':'excited',blink2:'idle',flap:'idle',hop:'happy',tilt:'lean',bob:'idle',wiggle:'wobble',lookl:'inspect',lookr:'inspect'};
  const resolve=(name,catalog)=>catalog.clips.find(c=>c.id===(Object.hasOwn(aliases,name)?aliases[name]:name))||null;

  /* Kandidatenpunkte getrennt nach „am Anker/Zeiger" und „frei in der Flaeche",
     damit ein etwas engerer Platz am Anker einer freien, aber weit entfernten
     Stelle vorgeht. */
  function spots(view,size,anchor,kind,baseline,hint,avoid){
    const gap=16,L=view.left||0,T=view.top||0,W=view.width,H=view.height,near=[],far=[];
    if(anchor){
      const mid=anchor.left+(anchor.width-size)/2;
      /* „on" ist der Rückfall für dichte Raster: lieber auf dem eigenen
         Element sitzen als über dem Text des Nachbarn schweben. */
      if(kind==='on')near.push([mid,anchor.top+(anchor.height-size)/2,false],[mid,anchor.top-size*baseline,true],[anchor.right-size*.9,anchor.top+(anchor.height-size)/2,false],[anchor.left+8,anchor.top+(anchor.height-size)/2,false]);
      else{
        if(kind==='edge')near.push([anchor.right-size*.85,anchor.top-size*baseline,true],[anchor.left+8,anchor.top-size*baseline,true],[mid,anchor.top-size*baseline,true]);
        near.push([anchor.right+14,anchor.top+Math.min(24,anchor.height/4),false],[anchor.left-size-14,anchor.top+Math.min(24,anchor.height/4),false],[mid,anchor.bottom+12,false],[mid,anchor.top-size-12,false]);
      }
    }
    if(hint)near.push([hint.x+34,hint.y-size*.55,false],[hint.x-size-34,hint.y-size*.55,false],[hint.x-size/2,hint.y+42,false],[hint.x-size/2,hint.y-size-42,false]);
    /* Frueher standen hier die vier Bildschirmecken, unten rechts zuerst. Ohne
       Anker fiel die Regie genau dorthin zurueck — die Figur sprang nach jeder
       Szene in dieselbe Ecke. Jetzt ist es ein Raster ueber die Inhaltsflaeche,
       deutlich vom Rand eingerueckt, Mitte zuerst, Eckpunkte zuletzt. */
    const ix=clamp(W*.12,gap,150),iy=clamp(H*.15,gap,150);
    const lx=L+ix,rx=L+W-size-ix,cx=L+(W-size)/2,ty=T+iy,by=T+H-size-iy,cy=T+(H-size)/2;
    far.push([cx,cy,false],[lx,cy,false],[rx,cy,false],[cx,ty,false],[cx,by,false],[lx,ty,false],[rx,ty,false],[lx,by,false],[rx,by,false]);
    /* Dazu die aeusseren Seitenraender. Das Raster oben liegt mitten im
       Textkoerper — ohne diese Punkte gab es ausserhalb des Satzspiegels gar
       keine Wahl, und die Figur stand fast immer ueber Text. */
    const gx=L+gap,gxr=L+W-size-gap;
    far.push([gx,cy,false],[gxr,cy,false],[gx,ty,false],[gxr,ty,false],[gx,by,false],[gxr,by,false]);
    /* Erst die letzte Position ausschliessen, dann mischen. Sortieren nach
       Abstand waere vorhersagbar — und vorhersagbar heisst hier: sie pendelt
       zwischen denselben zwei Punkten hin und her. */
    if(avoid){
      const keep=far.filter(q=>Math.hypot(q[0]-avoid.x,q[1]-avoid.y)>size*1.4);
      if(keep.length){far.length=0;far.push(...keep);}
    }
    for(let i=far.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));const t=far[i];far[i]=far[j];far[j]=t;}
    return {near,far};
  }
  /* Liefert immer einen Punkt, solange er überhaupt ins Sichtfeld passt.
     `clear` sagt, ob er frei von interaktiven Elementen ist. */
  /* Zwei Sorten Hindernis. Hart sind Bedienflaechen — da darf sie nie stehen.
     Weich ist Fliesstext: darueber zu schweben ist nicht kaputt, aber laestig.
     Weich zaehlt deshalb mit, wiegt aber weniger; gibt es gar keinen freien
     Platz, gewinnt der am wenigsten verdeckte. */
  function placement(view,size,obstacles,anchor,kind='safe',baseline=.927,hint=null,avoid=null,soft=null){
    const gap=16,L=view.left||0,T=view.top||0,W=view.width,H=view.height;
    if(W-size-gap*2<0||H-size-gap*2<0)return null;
    const {near,far}=spots(view,size,anchor,kind,baseline,hint,avoid);
    let best=null;
    const scan=(list,grow)=>{
      for(const [rx,ry,edge]of list){
        const h=size*(edge?baseline:1);
        const x=clamp(rx,L+gap,L+W-size-gap),y=clamp(ry,T+gap,T+H-h-gap);
        const rect={left:x,top:y,right:x+size,bottom:y+h};
        let hard=0;for(const r of obstacles)hard+=area(rect,r,grow);
        let text=0;if(soft)for(const r of soft)text+=area(rect,r,grow);
        if(hard===0&&text===0)return{x,y,edge,rect,clear:true};
        const score=hard*4+text;
        if(!best||score<best.score)best={x,y,edge,rect,clear:hard===0,score};
      }
      return null;
    };
    for(const [list,grow] of [[near,8],[near,2],[far,8],[near,-6],[far,-6]]){
      const hit=scan(list,grow);if(hit)return hit;
    }
    return best;
  }
  function travelPath(view,size,obstacles,origin,distance){
    if(!origin||origin.edge)return null;
    for(const dx of [-distance,distance]){
      const corridor={left:Math.min(origin.x,origin.x+dx),right:Math.max(origin.x,origin.x+dx)+size,top:origin.y,bottom:origin.y+size};
      if(corridor.left<(view.left||0)+16||corridor.right>(view.left||0)+view.width-16)continue;
      if(!obstacles.some(r=>intersects(corridor,r,6)))return {x:origin.x,y:origin.y,dx};
    }
    return null;
  }
  const api={clamp,intersects,area,permitted,frameAt,validImage,resolve,placement,travelPath,spots};
  if(typeof module!=='undefined'&&module.exports)module.exports=api;
  if(typeof document==='undefined')return;

  const legacySay=window.PhobosMascot?.say;
  window.PhobosMascot?.destroy?.();window.Moonling?.destroy?.();
  const root=document.querySelector('[data-moonling]'),catalog=window.MoonlingCatalog,motion=window.MoonlingMotion;
  if(!root||!catalog||!motion)return;
  const abort=new AbortController(),signal=abort.signal;
  const reduce=matchMedia('(prefers-reduced-motion: reduce)'),mobile=matchMedia('(max-width: 767px)'),fine=matchMedia('(pointer: fine)');
  const actor=root.querySelector('[data-ml-actor]'),poster=actor.querySelector('img'),canvas=actor.querySelector('canvas');
  const ctx=canvas.getContext('2d');
  const urls=JSON.parse(root.querySelector('[data-ml-assets]').textContent);
  const stage=document.querySelector('[data-moonling-stage]');
  /* Eigene Ebene, fest im Sichtfeld und beschnitten. Vorher lag die Figur
     absolut im Dokument: sie konnte die Seite nach unten verlaengern und stand
     bei Reiseszenen halb ausserhalb. Beides ist damit ausgeschlossen. */
  let layer=null;
  if(!stage){
    layer=document.createElement('div');layer.className='moonling-layer';
    root.append(layer);layer.append(actor);
  }
  const fx=document.createElement('canvas'),hands=document.createElement('img'),particles=new motion.Particles();
  fx.className='moonling-fx';fx.setAttribute('aria-hidden','true');hands.className='moonling-hands';hands.alt='';hands.width=192;hands.height=48;hands.hidden=true;
  (stage||layer||root).append(fx,hands);const fxctx=fx.getContext('2d');
  const state={enabled:root.dataset.enabled!=='false',variant:root.dataset.variant==='bare'?'bare':'cape',hidden:false,paused:false,reduced:reduce.matches,background:document.hidden,blocked:false,mobileAllowed:true,current:'idle',phase:'rest',loading:false,playing:false,away:false,effects:root.dataset.effects!=='false'};
  /* Schluessel angehoben: wer in den Tests einmal auf „Ausblenden" geklickt
   hat, haette die Figur ohne sichtbare Leiste nie zurueckbekommen. */
  const KEY='phobos-mascot-v19';
  try{const p=JSON.parse(localStorage.getItem(KEY));state.paused=p?.paused===true;state.hidden=p?.hidden===true;}catch(_){}
  let epoch=0,raf=0,timer=0,softTimer=0,scrollIdle=0,leaveTimer=0,ticks=0,layoutVersion=0,lastActive=Date.now(),lastEvent=0,active=null,anchor=null,destroyed=false,lastIdle='',lastSize=132,origin=null,edgeY=null,focusPinned=true,pointer=null,lastScene='',lastPoint=null;
  const cache=new Map(),timeouts=new Set(),cancelLoads=new Set();

  /* Nur echte Bedien- und Sperrflächen sind Hindernisse. Fließtext und
     Überschriften waren es früher auch — dadurch blieb praktisch kein Platz
     übrig und die Figur blendete sich aus. */
  const obstacleSelector='a,button,input,select,textarea,summary,label[for],iframe,video,model-viewer,[role="button"],[role="dialog"],[data-moonling-avoid],.cc,.satc,.ph-cart-note';
  /* Fliesstext: kein Hindernis im harten Sinn, aber nichts, was man verdecken
     will. Frueher zaehlte er gar nicht — dadurch stand die Figur mitten im
     Absatz, den gerade jemand liest. */
  const SOFT_SELECTOR='p,h1,h2,h3,h4,li,blockquote,figcaption,dt,dd,time,td,th';
  const SCENE_SELECTOR='[data-moonling-scene],.prod,.ph-home__hotspot,.ph-brand__logo,.ph-brand__shop,.map';
  /* Zweite Wahl, wenn gerade keine Szenenflaeche im Bild steht: irgendein
     Stueck Inhalt. Ohne das fiel die Regie auf „gar kein Anker" zurueck — und
     ohne Anker gibt es nur noch freie Flaeche, also Rand. */
  const CONTENT_SELECTOR='h1,h2,h3,figure,.lede,.sheet,.card,.ph-event,.applink,.lg-figs>div,.wir-values>div,blockquote,.rte>p,.rte>ul';
  const visible=el=>{
    if(!el?.isConnected||el.hidden)return false;
    const r=el.getBoundingClientRect(),style=getComputedStyle(el);
    return r.width>0&&r.height>0&&r.bottom>0&&r.top<innerHeight&&style.visibility!=='hidden'&&style.display!=='none';
  };
  /* Je Kontext mehrere passende Szenen — sonst spielt an derselben Stelle
     immer dasselbe ab und wirkt wie eine Schleife. */
  const POOLS={prod:['inspect','perch','cards'],path:['hover','excited','lean'],spot:['map','peek','surprised'],logo:['lean','sit','happy'],shop:['excited','happy','wobble'],foot:['sleep','sit'],cart:['treasure','happy']};
  function pick(pool){const list=pool.length>1?pool.filter(id=>id!==lastScene):pool;return list[Math.floor(Math.random()*list.length)];}
  const sceneFor=el=>el.dataset?.moonlingScene||pick(el.matches('.prod')?POOLS.prod:el.matches('.map,.ph-home__hotspot')?POOLS.spot:el.matches('.ph-brand__logo')?POOLS.logo:el.matches('.ph-brand__shop')?POOLS.shop:el.matches('.ph-cart-note')?POOLS.cart:el.matches('.ph-foot')?POOLS.foot:POOLS.path);
  /* Kein Zeigerbezug mehr: die Figur soll eigenstaendig durch die Seite
     wandern. Zur Maus zu springen las sich wie Teleportieren. */
  const freshPointer=()=>null;
  /* Auf der Startseite steckt die Figur bereits im Hero-Bild. Steht die
     Animation daneben still, gibt es sie zweimal. Deshalb dort nur Szenen,
     die durchs Bild laufen — kein Sitzen, kein Danebenstehen. */
  const onlyTravel=root.dataset.only==='travel';
  /* Standszenen verschwinden nach kurzer Zeit wieder. Stehenbleiben war das,
     was als stoerend auffiel: sie blieb vor dem Text, den man lesen wollte. */
  const STAND_MS=3200;

  function emit(){document.dispatchEvent(new CustomEvent('moonling:state',{detail:{...state}}));}
  function remember(){try{localStorage.setItem(KEY,JSON.stringify({paused:state.paused,hidden:state.hidden}));}catch(_) {}}
  function setPoster(){poster.src=urls[state.variant].poster;poster.width=192;poster.height=192;hands.src=urls[state.variant].grip;}
  function clearEffects(){particles.clear();hands.hidden=true;fxctx?.clearRect(0,0,fx.width,fx.height);}
  /* Positionierung über transform — kein Layout je Einzelbild. */
  /* Kurzer Auftritt, wenn die Figur an einer neuen Stelle erscheint. */
  let lastAt=null;
  function arrive(){
    const at=origin?Math.round(origin.x)+'/'+Math.round(origin.y):null;
    if(at===lastAt)return;lastAt=at;
    actor.classList.remove('is-arriving');void actor.offsetWidth;actor.classList.add('is-arriving');
    /* Die Klasse wieder abraeumen: `both` laesst sonst opacity:1 der Animation
       dauerhaft stehen und ueberschreibt spaeteres Ausblenden. */
    actor.addEventListener('animationend',()=>actor.classList.remove('is-arriving'),{once:true});
  }
  /* x und y sind Dokumentkoordinaten. Die Umrechnung passiert einmal beim
     Platzieren, nicht je Einzelbild — sonst wandert die Figur beim Scrollen
     mit dem Sichtfeld statt mit der Seite. */
  /* Abgang: erst ausblenden, dann aus dem Weg. Hart auf hidden sah aus wie
     ein Aussetzer. */
  function leave(){
    clearTimeout(leaveTimer);
    actor.classList.add('is-leaving');
    leaveTimer=setTimeout(()=>{actor.hidden=true;actor.classList.remove('is-leaving');lastAt=null;},300);
    timeouts.add(leaveTimer);
  }
  function stay(){clearTimeout(leaveTimer);actor.classList.remove('is-leaving');}
  /* x und y sind Dokumentkoordinaten. Die Ebene steht fest im Sichtfeld,
     deshalb wird hier der Scrollstand abgezogen — dadurch bleibt die Figur an
     ihrem Platz auf der Seite und wandert beim Scrollen mit dem Inhalt. */
  let letzteLage=null;
  function moveTo(x,y,angle=0){
    letzteLage={x,y,angle};
    const o=pageOffset();
    actor.style.transform='translate3d('+Math.round((x-o.x)*100)/100+'px,'+Math.round((y-o.y)*100)/100+'px,0)'+(angle?' rotate('+angle+'rad)':'');
  }
  /* Laeuft gerade keine Szene, ruft niemand moveTo — dann bliebe die Figur beim
     Scrollen am Sichtfeld haengen. Deshalb wird die letzte Lage nachgezogen. */
  let scrollBild=0;
  function lageNachziehen(){
    if(scrollBild||!letzteLage||stage)return;
    scrollBild=requestAnimationFrame(()=>{scrollBild=0;if(letzteLage)moveTo(letzteLage.x,letzteLage.y,letzteLage.angle);});
  }
  function stop(showPoster=true){
    epoch++;cancelAnimationFrame(raf);raf=0;active=null;state.loading=false;state.playing=false;state.phase='rest';stay();
    if(showPoster){canvas.hidden=true;poster.hidden=false;}
    actor.style.opacity='';actor.style.clipPath='';state.current='idle';clearEffects();
  }
  function overlays(){return [...document.querySelectorAll('dialog[open],[aria-modal="true"],#ph-cookie:not([hidden]),.ph-brand__mobile[open]')].some(visible);}
  const stageRect=()=>stage?stage.getBoundingClientRect():null;
  function toLocal(r){const b=stageRect();return b?{left:r.left-b.left,top:r.top-b.top,right:r.right-b.left,bottom:r.bottom-b.top,width:r.width,height:r.height}:r;}
  /* `skip` nimmt das Element aus, auf dem die Figur gerade sitzen soll —
     sonst blockiert die Produktkarte genau die Szene, die zu ihr gehört. */
  /* Verschiebung vom Sichtfeld ins Dokument. Ohne Buehne rechnen wir in
     Dokumentkoordinaten, damit die Figur beim Scrollen an ihrem Platz bleibt. */
  /* Dokumentkoordinaten: die Figur gehoert in die Seite und scrollt mit ihr
     weg. Kurzzeitig rechnete hier alles im Sichtfeld — damit klebte sie
     dauerhaft vor dem Inhalt wie eine Einblendung, statt eingebettet zu sein.
     Die feste Ebene bleibt trotzdem (sie verhindert, dass die Seite waechst);
     die Umrechnung ins Sichtfeld passiert erst beim Zeichnen in moveTo. */
  const pageOffset=()=>stage?{x:0,y:0}:{x:window.scrollX||0,y:window.scrollY||0};
  /* Hoehe der klebenden Kopfzeile. Sie liegt ueber der Figur; wer darunter
     platziert wird, steht halb abgeschnitten da. */
  const barHeight=()=>{
    if(stage)return 0;
    const bar=document.querySelector('.ph-brand-wrap,.ph-brand');
    if(!bar)return 0;
    const r=bar.getBoundingClientRect();
    /* Die Kopfzeile schwebt jetzt fest oben statt zu kleben. Frueher zaehlte
       sie nur, wenn ihre Oberkante bei 0 lag — das trifft nie mehr zu. */
    const fest=getComputedStyle(bar).position==='fixed';
    if(fest||r.top<=1)return Math.max(0,Math.ceil(r.bottom)+8);
    return 0;
  };
  function geometry(skip){
    const vp=window.visualViewport,b=stageRect();
    const bar=barHeight();
    const view=b?{left:0,top:0,width:b.width,height:b.height}:{left:vp?.offsetLeft||0,top:(vp?.offsetTop||0)+bar,width:vp?.width||innerWidth,height:(vp?.height||innerHeight)-bar};
    const keep=el=>!el.closest('[data-moonling]')&&el!==actor&&el!==hands&&el!==fx&&(!stage||stage.contains(el))&&!(skip&&(el===skip||skip.contains(el)||el.contains(skip)))&&visible(el);
    const rects=sel=>[...document.querySelectorAll(sel)].filter(keep).map(el=>toLocal(el.getBoundingClientRect()));
    return {view,obstacles:rects(obstacleSelector),soft:rects(SOFT_SELECTOR)};
  }
  function sizeFor(view){
    const raw=Number(stage?stage.dataset.size:mobile.matches?root.dataset.mobileSize:root.dataset.size)||(mobile.matches?104:132);
    return Math.round(clamp(raw,56,Math.max(56,Math.min(260,view.width-40,view.height-40))));
  }
  function place(clip,at,hint,hold){
    if(destroyed)return false;
    if(!state.enabled||state.hidden||!state.mobileAllowed||state.blocked||state.background||(!clip&&state.away)){actor.hidden=true;hands.hidden=true;return false;}
    const edgeScene=(clip?.placement||'safe')==='edge';
    const {view,obstacles,soft}=geometry(edgeScene?at:null);
    const size=sizeFor(view);
    lastSize=size;actor.style.setProperty('--moonling-size',size+'px');
    if(stage){if(actor.parentElement!==stage)stage.append(actor);actor.style.position='absolute';fx.style.position='absolute';hands.style.position='absolute';}
    if(document.activeElement===actor&&origin&&focusPinned)return true;
    /* Steht sie schon frei und im Bild, bleibt sie stehen — kein Zucken.
       `hold` zieht das auf Reaktionen aus: wer sie antippt, bekommt eine
       Antwort an Ort und Stelle statt eines Sprungs. Die Regie selbst sucht
       sich dagegen jedes Mal einen neuen Platz. */
    if((hold||!clip)&&!at&&origin&&!edgeScene&&!clip?.travel){
      const o=pageOffset();
      const vr={left:origin.rect.left-o.x,top:origin.rect.top-o.y,right:origin.rect.right-o.x,bottom:origin.rect.bottom-o.y};
      if(vr.left>=view.left+12&&vr.top>=view.top+12&&vr.right<=view.left+view.width-12&&vr.bottom<=view.top+view.height-12&&!obstacles.some(r=>intersects(vr,r,origin.edge?0:6))){actor.hidden=false;if(!origin.edge)actor.style.clipPath='';return true;}
    }
    const a=visible(at)?toLocal(at.getBoundingClientRect()):null;
    const baseline=clip&&['peek','hide'].includes(clip.id)?.64:.927;
    let point=placement(view,size,obstacles,a,clip?.placement||'safe',baseline,hint||freshPointer(),a?null:lastPoint,soft);
    /* Kein freier Platz daneben? Dann auf dem Element selbst — sichtbar,
       absichtlich wirkend und ohne fremden Text zu verdecken. */
    if(a&&(!point||!point.clear)&&!edgeScene){
      const onIt=placement(view,size,geometry(at).obstacles,a,'on',baseline,null,null,null);
      if(onIt?.clear)point={...onIt,on:true};
    }
    if(stage&&!a&&!clip?.travel){const x=(view.width-size)/2,y=(view.height-size)/2;point={x,y,edge:false,clear:true,rect:{left:x,top:y,right:x+size,bottom:y+size}};}
    actor.hidden=!point;if(!point)return false;stay();
    /* Sitzt sie auf einem Element oder überlappt sie im Notfall etwas, darf
       sie keine Klicks abfangen — das Element darunter bleibt bedienbar. */
    actor.style.pointerEvents=point.clear&&!point.edge&&!point.on?'':'none';
    const off=pageOffset();
    lastPoint={x:point.x,y:point.y};
    origin={...point,x:point.x+off.x,y:point.y+off.y,
      rect:{left:point.rect.left+off.x,top:point.rect.top+off.y,right:point.rect.right+off.x,bottom:point.rect.bottom+off.y}};
    edgeY=point.edge&&a?a.top+off.y:null;moveTo(origin.x,origin.y);
    actor.style.clipPath=point.edge?'inset(0 0 '+((1-baseline)*100)+'% 0)':'';return true;
  }
  function load(clip,variant){
    const key=variant+'/'+clip.id;
    if(cache.has(key)){const value=cache.get(key);cache.delete(key);cache.set(key,value);return value;}
    const url=urls[variant]?.[clip.id];if(!url)return Promise.resolve(null);
    const pending=new Promise(resolve=>{
      const image=new Image();image.decoding='async';let settled=false;
      const cancel=()=>complete(null);
      const complete=value=>{if(settled)return;settled=true;clearTimeout(timeout);timeouts.delete(timeout);cancelLoads.delete(cancel);resolve(value);};
      const timeout=setTimeout(()=>{cache.delete(key);complete(null);},12000);timeouts.add(timeout);
      cancelLoads.add(cancel);
      /* decode() vor dem ersten Zeichnen — sonst stockt das erste Bild. */
      image.onload=()=>{const ok=validImage(image,clip);if(!ok){cache.delete(key);complete(null);return;}
        (image.decode?image.decode().catch(()=>{}):Promise.resolve()).then(()=>complete(image));};
      image.onerror=()=>{cache.delete(key);complete(null);};image.src=url;
    });
    cache.set(key,pending);while(cache.size>10)cache.delete(cache.keys().next().value);
    return pending;
  }
  /* Vorwaermen: nur die eine Szene, die am haeufigsten zuerst kommt. Frueher
     waren es vier (idle, sit, inspect, hover) — nachgemessen 1,09 MB in den
     ersten acht Sekunden, auf jeder Seite, fuer jeden Besuch, bevor die Figur
     ueberhaupt etwas tut. Die uebrigen Szenen laedt play() ohnehin selbst,
     wenn sie dran sind; bis dahin steht das Standbild da.
     Bei Datensparmodus oder langsamer Leitung wird gar nichts vorgewaermt. */
  function warm(){
    if(destroyed||!permitted(state))return;
    const c=navigator.connection;
    if(c&&(c.saveData||/(^|-)2g$|^3g$/.test(c.effectiveType||'')))return;
    const clip=resolve('idle',catalog);if(clip)load(clip,state.variant);
  }
  async function play(name,options={}){
    if(options?.nodeType)options={anchor:options};
    const clip=resolve(name,catalog);
    if(!clip||!permitted(state)||destroyed||!ctx)return false;
    if(onlyTravel&&!clip.travel&&!options.force)return false;
    if(active&&active.priority>clip.priority&&!options.force)return false;
    stop(false);state.away=false;
    anchor=options.anchor||(stage&&['peek','hide','sit','perch','cards','sleep'].includes(clip.id)?stage.querySelector('[data-moonling-anchor="edge"]'):null);
    const hint=options.hint||freshPointer();
    if(!place(clip,anchor,hint,options.hold))return false;
    const space=geometry(clip.placement==='edge'?anchor:null);
    const pinned=document.activeElement===actor&&focusPinned;
    const route=clip.travel&&!pinned&&options.travel!==false?motion.route(space.view,lastSize,space.obstacles,origin,clip):null;
    /* Kein Weg frei? Dann gar nicht erst auftauchen. place() hat die Figur
       oben schon sichtbar gemacht — ohne diesen Abgang blieb sie als
       Standbild stehen, mitten in der Seite. */
    if(clip.travel&&!route&&!pinned&&options.travel!==false){state.phase='no-route';state.away=true;leave();emit();return false;}
    const token=epoch,variant=state.variant;active=clip;state.current=clip.id;state.loading=true;lastScene=clip.id;emit();
    const image=await load(clip,variant);
    if(destroyed||token!==epoch||variant!==state.variant||!permitted(state))return false;
    state.loading=false;
    if(!image){stop();emit();return false;}
    const routeVersion=layoutVersion;
    const cell=image.naturalWidth/clip.cols;let first=null,previous=-1;
    const initial={...origin},initialEdge=edgeY,dpr=Math.min(window.devicePixelRatio||1,1.5);
    /* Verankerte Standszenen wandern beim Scrollen mit ihrem Element mit. */
    const anchorEl=anchor,view=space.view;
    let follow=!route&&!!anchorEl&&visible(anchorEl),offset=null;
    if(follow){const o=pageOffset(),lr=toLocal(anchorEl.getBoundingClientRect());offset={dx:initial.x-(lr.left+o.x),dy:initial.y-(lr.top+o.y),dEdge:initialEdge===null?null:initialEdge-(lr.top+o.y)};}
    fx.width=Math.ceil((view.left+view.width)*dpr);fx.height=Math.ceil((view.top+view.height)*dpr);fx.style.width=(view.left+view.width)+'px';fx.style.height=(view.top+view.height)+'px';/* fx liegt in derselben festen Ebene und ist sichtfeldgross — es bleibt bei 0,0. */
    fx.style.transform='translate3d(0,0,0)';
    fxctx?.setTransform(dpr,0,0,dpr,0,0);
    canvas.width=192;canvas.height=192;poster.hidden=true;canvas.hidden=false;actor.hidden=false;state.playing=true;actor.dataset.scene=clip.id;arrive();emit();
    function draw(now){
      if(token!==epoch)return;
      if(!permitted(state)||!root.isConnected||routeVersion!==layoutVersion||(route&&document.activeElement===actor&&focusPinned)){stop();state.away=false;place();emit();return;}
      if(first===null)first=now;
      const elapsed=now-first;
      let base=initial,edgeNow=initialEdge;
      if(follow){
        if(!visible(anchorEl))follow=false;
        else{
          const o=pageOffset(),lr=toLocal(anchorEl.getBoundingClientRect());
          base={...initial,x:lr.left+o.x+offset.dx,y:lr.top+o.y+offset.dy};
          edgeNow=offset.dEdge===null?null:lr.top+o.y+offset.dEdge;
        }
      }
      const sample=motion.sample(clip,elapsed,{path:route,size:lastSize,origin:base,edge:edgeNow});
      /* Reiseszenen laufen zu Ende — sie tragen die Figur ohnehin aus dem
         Bild. Standszenen brechen nach STAND_MS ab, auch wenn der Clip
         laenger ist. Danach geht die Figur in beiden Faellen. */
      if(sample.done||(!route&&elapsed>STAND_MS)){
        cancelAnimationFrame(raf);raf=0;active=null;state.playing=false;state.loading=false;state.phase='rest';clearEffects();
        state.away=true;leave();anchor=null;emit();return;
      }
      if(state.phase!==sample.phase){state.phase=sample.phase;emit();}
      moveTo(sample.x,sample.y,sample.angle);
      actor.style.clipPath=sample.cut===null?'':'inset(0 0 '+(100-sample.cut*100)+'% 0)';
      origin={x:sample.x,y:sample.y,edge:initial.edge,clear:initial.clear,rect:{left:sample.x,top:sample.y,right:sample.x+lastSize,bottom:sample.y+lastSize*(sample.cut??1)}};
      hands.hidden=sample.hands<=0;
      if(sample.hands>0){hands.style.opacity=String(sample.hands);{const ho=pageOffset();hands.style.transform='translate3d('+(base.x-ho.x)+'px,'+(((edgeNow??base.y)-lastSize*.1)-ho.y)+'px,0)';}hands.style.width=lastSize+'px';hands.style.height=lastSize*.25+'px';}
      if(fxctx){const o=pageOffset();fx.style.transform='translate3d(0,0,0)';fxctx.clearRect(0,0,fx.width/dpr,fx.height/dpr);particles.draw(fxctx,elapsed,{...sample,x:sample.x-o.x,y:sample.y-o.y},clip,lastSize,state.effects);}
      const frame=sample.frame;
      if(frame!==previous){
        ctx.clearRect(0,0,192,192);
        ctx.save();if(sample.direction<0){ctx.translate(192,0);ctx.scale(-1,1);}ctx.drawImage(image,frame%clip.cols*cell,Math.floor(frame/clip.cols)*cell,cell,cell,0,0,192,192);ctx.restore();
        ctx.globalAlpha=1;ctx.globalCompositeOperation='source-over';previous=frame;
      }
      raf=requestAnimationFrame(draw);
    }
    raf=requestAnimationFrame(draw);return true;
  }
  function legacy(){
    document.querySelectorAll('[data-moonling-poster],[data-mascot-fig] .pm__img,.ph-cart-note img,img.ph-mascot[src*="mascot-cape"]').forEach(img=>{
      const src=urls[state.variant].poster;if(img.getAttribute('src')!==src){img.src=src;img.width=192;img.height=192;}
    });
  }
  function sync(){
    state.reduced=reduce.matches;state.background=document.hidden;state.blocked=overlays();state.mobileAllowed=!mobile.matches||root.dataset.mobile!=='false';
    document.documentElement.dataset.phMascot=state.hidden?'hidden':permitted(state)?'active':'paused';
    if(!permitted(state))stop();
    const pause=root.querySelector('[data-ml-pause]'),hide=root.querySelector('[data-ml-hide]');
    pause.disabled=state.reduced;pause.textContent=state.reduced?root.dataset.reducedLabel:state.paused?root.dataset.resumeLabel:root.dataset.pauseLabel;
    pause.setAttribute('aria-pressed',String(state.paused));pause.setAttribute('aria-label',state.reduced?root.dataset.reducedLabel:state.paused?root.dataset.resumeA11y:root.dataset.pauseA11y);hide.textContent=state.hidden?root.dataset.showLabel:root.dataset.hideLabel;hide.setAttribute('aria-label',state.hidden?root.dataset.showA11y:root.dataset.hideA11y);hide.setAttribute('aria-pressed',String(state.hidden));
    legacy();
    if(!state.playing&&!state.loading)place(active,anchor);
    emit();
  }
  /* Weich: nur Zustand nachziehen. Hart: laufende Reiseszenen verwerfen. */
  function soft(){if(!softTimer)softTimer=setTimeout(()=>{softTimer=0;sync();},280);}
  function relayout(){layoutVersion++;soft();}

  /* --- Regie: wo taucht sie als Nächstes auf? --- */
  function spot(){
    const pool=[...document.querySelectorAll(SCENE_SELECTOR)].filter(el=>{
      if(el.closest('[data-moonling]')||!visible(el))return false;
      const r=el.getBoundingClientRect();
      /* Nur was gerade wirklich im Bild steht — sie soll dort auftauchen,
         wo gelesen wird, und dann mit dem Inhalt weiterscrollen. */
      return r.height>36&&r.top>0&&r.bottom<innerHeight;
    });
    if(pool.length)return pool[Math.floor(Math.random()*pool.length)];
    /* Nichts Passendes vollstaendig im Bild? Dann reicht ein Stueck Inhalt,
       das den Bildausschnitt schneidet — Hauptsache ein Bezug. */
    const rest=[...document.querySelectorAll(CONTENT_SELECTOR)].filter(el=>{
      if(el.closest('[data-moonling]')||!visible(el))return false;
      const r=el.getBoundingClientRect();
      return r.height>28&&r.height<innerHeight*.9&&r.bottom>64&&r.top<innerHeight-64;
    });
    return rest.length?rest[Math.floor(Math.random()*rest.length)]:null;
  }
  function idle(first=false){
    clearTimeout(timer);if(root.dataset.auto==='false'||destroyed||window.Shopify?.designMode)return;
    timer=setTimeout(()=>{
      if(permitted(state)&&!active&&!state.loading&&onlyTravel){
        play(['wander','sail','surf','mission'][Math.floor(Math.random()*4)],{travel:true});
      }
      else if(permitted(state)&&!active&&!state.loading){
        const footer=document.querySelector('.ph-foot,[data-moonling-anchor="footer"]');
        if(Date.now()-lastActive>45000&&visible(footer))play('sleep',{anchor:footer});
        else{
          ticks++;
          /* Reise-Szenen beginnen ausserhalb des Bildes und tragen die Figur quer
             darueber. Als Regelfall wirkt das unruhig — deshalb hoechstens jede
             achte Runde und nur, wenn seltene Szenen erlaubt sind. */
          const mayTravel=root.dataset.special==='true'&&ticks%8===0;
          const el=spot();
          if(el&&!mayTravel&&Math.random()<.82)play(sceneFor(el),{anchor:el});
          else if(mayTravel)play(['wander','sail','surf','mission'][Math.floor(Math.random()*4)],{travel:true});
          else{
            const pool=['idle','sit','peek','lean','inspect'].filter(id=>id!==lastIdle);
            const id=pool[Math.floor(Math.random()*pool.length)];lastIdle=id;
            play(id,{anchor:el||document.querySelector('[data-moonling-anchor="edge"],.ph-home__scene')});
          }
        }
      }
      idle();
    /* Im Nur-Reise-Modus dauert eine Querung schon 9 bis 14 Sekunden. Mit dem
       normalen Takt waere die Figur die halbe Zeit im Bild — deshalb dort
       deutlich groessere Abstaende. */
    },first?3200:onlyTravel?26000+Math.random()*18000:15000+Math.random()*11000);
  }
  function trigger(event){
    if(onlyTravel||Date.now()-lastEvent<5200)return;
    const el=event.target.closest?.(SCENE_SELECTOR);
    if(!el||el===actor||el.closest('[data-moonling]'))return;
    if(event.type==='pointerover'&&(!fine.matches||el.contains(event.relatedTarget)))return;
    lastEvent=Date.now();
    play(sceneFor(el),{anchor:el});
  }

  setPoster();root.querySelector('[data-ml-controls]').hidden=false;
  root.querySelector('[data-ml-pause]').addEventListener('click',()=>{state.paused=!state.paused;remember();sync();if(!state.paused)idle(true);},{signal});
  root.querySelector('[data-ml-hide]').addEventListener('click',()=>{state.hidden=!state.hidden;state.away=false;remember();sync();if(!state.hidden)idle(true);},{signal});
  /* Antippen loest eine kurze Reaktion an Ort und Stelle aus. Frueher lief hier
     „zoom" — eine Reise-Szene, die die Figur vom Bildschirm traegt. Das las sich
     wie ein Abbruch statt wie eine Antwort. */
  actor.addEventListener('click',event=>{
    focusPinned=event.detail===0;
    const pool=['happy','surprised','wobble','spin'].filter(id=>id!==lastScene);
    play(pool[Math.floor(Math.random()*pool.length)],{force:true,travel:false,anchor,hold:true});
  },{signal});
  actor.addEventListener('blur',soft,{signal});
  document.addEventListener('phobos:cart-added',()=>play('treasure',{anchor:document.querySelector('.ph-cart-note'),force:true}),{signal});
  document.addEventListener('moonling:play',event=>{if(event.detail?.scene)play(event.detail.scene,{anchor:event.detail.anchor,force:event.detail.force});},{signal});
  document.addEventListener('pointerover',trigger,{passive:true,signal});document.addEventListener('focusin',trigger,{signal});
  document.addEventListener('pointermove',event=>{if(event.pointerType!=='touch')pointer={x:event.clientX,y:event.clientY,t:Date.now()};},{passive:true,signal});
  document.addEventListener('pointerdown',()=>{lastActive=Date.now();focusPinned=false;},{passive:true,signal});
  document.addEventListener('keydown',event=>{lastActive=Date.now();focusPinned=true;if(event.key==='Escape'){stop();state.away=false;place();emit();}},{signal});
  document.addEventListener('visibilitychange',()=>{sync();if(!document.hidden)idle(true);},{signal});
  /* Scrollen bricht nichts mehr ab: verankerte Szenen laufen weiter und
     folgen ihrem Element, freistehende bleiben stehen. */
  /* Nach dem Scrollen wieder dorthin, wo gelesen wird. Ohne das steht die
     Figur bis zur naechsten Regierunde (bis zu 26 s) ausserhalb des Bildes.
     Bewusst erst nach dem Stillstand und nur, wenn gerade nichts laeuft —
     Nachrechnen pro Einzelbild hatte sie frueher wegdriften lassen. */
  window.addEventListener('scroll',()=>{
    lastActive=Date.now();clearTimeout(scrollIdle);
    /* Waehrend des Scrollens die zuletzt gezeichnete Lage nachziehen. Ohne das
       klebt die Figur am Sichtfeld, solange keine Szene laeuft. */
    lageNachziehen();
    scrollIdle=setTimeout(()=>{
      if(destroyed||active||state.playing||state.loading||!origin)return;
      const o=pageOffset(),{view}=geometry();
      const t=origin.rect.top-o.y,b=origin.rect.bottom-o.y,l=origin.rect.left-o.x,r=origin.rect.right-o.x;
      if(t<view.top+12||b>view.top+view.height-12||l<view.left+12||r>view.left+view.width-12)place();
    },650);
  },{passive:true,signal});
  window.addEventListener('resize',relayout,{passive:true,signal});window.visualViewport?.addEventListener('resize',relayout,{passive:true,signal});
  window.visualViewport?.addEventListener('scroll',()=>{lastActive=Date.now();},{passive:true,signal});
  reduce.addEventListener('change',sync,{signal});mobile.addEventListener('change',relayout,{signal});
  document.addEventListener('shopify:section:load',relayout,{signal});document.addEventListener('shopify:section:unload',relayout,{signal});document.addEventListener('shopify:section:reorder',relayout,{signal});
  window.addEventListener('storage',event=>{if(event.key!==KEY)return;try{const p=JSON.parse(event.newValue);state.hidden=p?.hidden===true;state.paused=p?.paused===true;sync();}catch(_){}},{signal});
  /* Nur Struktur- und Sichtbarkeitsänderungen, kein `class` — das feuerte bei
     jedem Hover und riss die Animation mitten im Bild ab. */
  const observer=new MutationObserver(records=>{
    for(const r of records){
      if(r.target.closest?.('[data-moonling]')||r.target.closest?.('[data-moonling-ui]'))continue;
      if(r.type==='childList'&&!r.addedNodes.length&&!r.removedNodes.length)continue;
      soft();return;
    }
  });
  observer.observe(document.body,{childList:true,subtree:true,attributes:true,attributeFilter:['hidden','open','aria-modal']});
  function destroy(){destroyed=true;abort.abort();stop();clearTimeout(timer);clearTimeout(softTimer);clearTimeout(scrollIdle);cancelLoads.forEach(cancel=>cancel());cancelLoads.clear();timeouts.forEach(clearTimeout);timeouts.clear();observer.disconnect();fx.remove();hands.remove();actor.hidden=true;root.querySelector('[data-ml-controls]').hidden=true;cache.clear();}
  window.Moonling={play,pause(value=true){state.paused=!!value;state.away=false;remember();sync();},hide(value=true){state.hidden=!!value;state.away=false;remember();sync();},setVariant(value){if(!['cape','bare'].includes(value))return false;stop();state.away=false;state.variant=value;setPoster();sync();return true;},setEffects(value){state.effects=!!value;if(!state.effects)clearEffects();emit();},getState:()=>({...state,version:'22.0.0',cached:cache.size,size:lastSize,scene:lastScene,origin:origin?{x:origin.x,y:origin.y,clear:origin.clear}:null}),getScenes:()=>catalog.clips.map(c=>({id:c.id,label:c.label,duration:c.duration,travel:c.travel})),stop(){stop();state.away=false;place();emit();},refresh(){stop();state.away=false;relayout();},destroy};
  window.PhobosMascot={play:(name,el)=>play(name,el?{anchor:el}:{}),boop:()=>play('surprised'),setMood:name=>play(name),say:legacySay,pause:window.Moonling.pause,hide:window.Moonling.hide,getState:window.Moonling.getState,destroy};
  /* Die Kopfzeile klebt oben und liegt ueber der Figur. Halb darunter
     abgeschnitten stehenzubleiben sieht kaputt aus — also blendet sie sich
     aus, sobald sie in den Bereich der Leiste geraet. */
  (function barGuard(){
    const bar=document.querySelector('.ph-brand-wrap,.ph-brand');
    if(!bar||!('IntersectionObserver' in window))return;
    const h=Math.ceil(bar.getBoundingClientRect().height)||80;
    const io=new IntersectionObserver(entries=>{
      /* Schwellwert 1: schon eine angeschnittene Kante blendet aus. Vorher griff
         es erst, wenn die Figur vollstaendig hinter der Leiste lag — genau der
         halb abgeschnittene Zustand blieb also sichtbar. */
      for(const e of entries)actor.classList.toggle('is-behind-bar',e.intersectionRatio<0.999);
    },{rootMargin:'-'+(h+6)+'px 0px 0px 0px',threshold:[0,0.5,0.999,1]});
    io.observe(actor);
    cancelLoads.add(()=>io.disconnect());
  })();
  sync();idle(true);
  (window.requestIdleCallback||(fn=>setTimeout(fn,1200)))(warm);
})();
